import { useId } from 'react';
import { Badge, Checkbox, Icon, Tabs, Toggle, type TabItem } from '../../../components/ui';
import { formatBRLFromCents, formatCount, formatRatio } from '../../../lib/format';
import { alternarItem, type FormularioEnvio } from '../formulario';
import type { AudienceSource, Segment } from '../types';
import { SEGMENT_FAMILY_ICON, SEGMENT_FAMILY_LABEL, SEGMENT_FAMILY_ORDER } from '../vocabulario';
import { ImportarCsv } from './ImportarCsv';

/**
 * "Contatos de:" -- publicos pos-fato da base ou CSV importado.
 *
 * Decisoes nao obvias:
 *  1. As duas origens sao abas porque sao EXCLUSIVAS: um envio sai para os
 *     segmentos OU para a lista, nunca para a uniao (a deduplicacao entre
 *     fontes seria invisivel para quem opera).
 *  2. Segmentos agrupados pela FAMILIA do fato (carrinho, comercial,
 *     expirados), cada um com o fato por extenso, o valor parado, quantos
 *     receberam envio nos ultimos 7 dias e a taxa historica de pagamento --
 *     e o que a pessoa precisa para escolher o publico mais quente sem abrir
 *     outra tela.
 *  3. Nao existe "toda a base" nem "clientes recentes": sem o fato nao ha
 *     recuperacao e, portanto, nao ha comissao. O texto no rodape diz isso,
 *     para ninguem procurar a opcao que "sumiu".
 */

const ORIGENS: readonly TabItem<AudienceSource>[] = [
  { value: 'segmentos', label: 'Públicos da base', icon: 'leads' },
  { value: 'csv', label: 'Importar CSV', icon: 'upload' },
];

export function SeletorDePublico({
  form,
  segments,
  temFunilExpirados,
  onChange,
}: {
  readonly form: FormularioEnvio;
  readonly segments: readonly Segment[];
  /** A empresa tem o funil de expirados ligado: diz POR QUE um publico D+X esta vazio. */
  readonly temFunilExpirados: boolean;
  readonly onChange: (patch: Partial<FormularioEnvio>) => void;
}): JSX.Element {
  const painelId = useId();
  const recentesDosMarcados = segments.filter((s) => form.segmentos.includes(s.key)).reduce((acc, s) => acc + s.recentlyContactedCount, 0);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium text-ink">
          Contatos de:<span aria-hidden="true" className="ml-0.5 text-brand">*</span>
        </p>
        <Badge tone="neutral" icon="escudo">
          Só depois do fato
        </Badge>
      </div>
      <Tabs items={ORIGENS} value={form.fonte} onChange={(fonte) => onChange({ fonte })} label="Origem dos contatos" size="sm" panelId={painelId} />
      <div id={painelId} role="tabpanel">
        {form.fonte === 'segmentos' ? (
          <div className="flex flex-col gap-4">
            {SEGMENT_FAMILY_ORDER.map((familia) => {
              const daFamilia = segments.filter((s) => s.family === familia);
              if (daFamilia.length === 0) {
                return null;
              }
              return (
                <fieldset key={familia} className="flex flex-col gap-2">
                  <legend className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-ink-subtle">
                    <Icon name={SEGMENT_FAMILY_ICON[familia]} size="xs" />
                    {SEGMENT_FAMILY_LABEL[familia]}
                  </legend>
                  {daFamilia.map((s) => {
                    const indisponivel = s.estimatedCount === 0;
                    const semFunil = s.family === 'expirados' && !temFunilExpirados;
                    return (
                      <Checkbox
                        key={s.key}
                        checked={form.segmentos.includes(s.key)}
                        disabled={indisponivel}
                        onChange={() => onChange({ segmentos: alternarItem(form.segmentos, s.key) })}
                        label={
                          <span className="flex items-center justify-between gap-3">
                            <span>{s.label}</span>
                            <span className="shrink-0 tabular-nums text-ink-subtle">{indisponivel ? (semFunil ? 'não se aplica' : 'ninguém agora') : formatCount(s.estimatedCount)}</span>
                          </span>
                        }
                        description={
                          <>
                            <span className="block">{s.fact}</span>
                            {indisponivel ? (
                              <span className="mt-0.5 block">
                                {semFunil ? 'A empresa não tem o funil de expirados ligado.' : 'Ninguém neste público agora: todo mundo voltou, pagou ou ainda está na régua.'}
                              </span>
                            ) : (
                              <span className="mt-0.5 block text-ink-muted">
                                {formatBRLFromCents(s.stalledAmountCents)} parados · {formatCount(s.recentlyContactedCount)} receberam envio nos últimos 7 dias ·{' '}
                                {s.benchmark === null ? 'sem histórico de envio' : `${formatRatio(s.benchmark.paid, s.benchmark.sent)} pagaram nos envios anteriores`}
                              </span>
                            )}
                          </>
                        }
                      />
                    );
                  })}
                </fieldset>
              );
            })}
            <div className="rounded-xl border border-line bg-elevated px-4 py-3">
              <Toggle
                checked={form.pularRecentes}
                onChange={(v) => onChange({ pularRecentes: v })}
                label="Pular quem recebeu envio em massa nos últimos 7 dias"
                description={
                  form.segmentos.length === 0
                    ? 'Duas mensagens na mesma semana é o caminho mais curto para o SAIR.'
                    : `${form.pularRecentes ? 'Tira' : 'Tiraria'} ${formatCount(recentesDosMarcados)} contatos dos públicos marcados. Duas mensagens na mesma semana é o caminho mais curto para o SAIR.`
                }
              />
            </div>
            <p className="text-xs text-ink-subtle">
              Não existe "toda a base" nem lembrete antes de vencer: todo público aqui já abandonou, teve o pagamento recusado, venceu ou respondeu sem fechar. É o que torna o
              retorno cobrável.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <ImportarCsv csv={form.csv} onCsv={(csv) => onChange({ csv })} />
            <p className="text-xs text-ink-subtle">
              Use só listas de quem já abandonou ou venceu (por exemplo, exportadas da plataforma de pagamento). Lista importada não tem histórico: a estimativa de retorno
              não aparece, só o custo.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
